/**
 * Enveloppe d'un bloc de leçon en mode édition : header (badge + actions),
 * corps (éditeur par type), barre « insérer après ».
 */

import { useState } from 'react';
import {
  BookOpen,
  Heading as HeadingIcon,
  Lightbulb,
  StickyNote,
  Text as TextIcon,
  type LucideIcon,
} from 'lucide-react';
import { cn, Button } from '@jeraisonne/ui';
import { renderLessonBlockEditor } from './BlockEditors';
import type { LessonBlock, LessonBlockType } from '@/lib/lesson-v2/types';

const TYPE_META: Record<
  LessonBlockType,
  { label: string; Icon: LucideIcon; border: string; icon: string }
> = {
  heading: {
    label: 'Titre',
    Icon: HeadingIcon,
    border: 'border-blue-200',
    icon: 'text-blue-700 bg-blue-50 ring-blue-200',
  },
  text: {
    label: 'Texte',
    Icon: TextIcon,
    border: 'border-slate-200',
    icon: 'text-slate-700 bg-slate-50 ring-slate-200',
  },
  key_point: {
    label: 'Point clé',
    Icon: Lightbulb,
    border: 'border-amber-200',
    icon: 'text-amber-700 bg-amber-50 ring-amber-200',
  },
  example: {
    label: 'Exemple',
    Icon: BookOpen,
    border: 'border-emerald-200',
    icon: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
  },
  note: {
    label: 'Note tuteur',
    Icon: StickyNote,
    border: 'border-slate-300',
    icon: 'text-slate-600 bg-slate-100 ring-slate-300',
  },
};

const INSERT_OPTIONS: Record<'content' | 'meta', { type: LessonBlockType; label: string }[]> = {
  content: [
    { type: 'text', label: 'Texte' },
    { type: 'heading', label: 'Titre' },
    { type: 'key_point', label: 'Point clé' },
    { type: 'example', label: 'Exemple' },
  ],
  meta: [{ type: 'note', label: 'Note' }],
};

export interface LessonBlockShellProps {
  block: LessonBlock;
  isFirst: boolean;
  isLast: boolean;
  isDisabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onInsertAfter: (type: LessonBlockType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onPatchData: (data: Record<string, unknown>) => void;
  onSplitRange: (start: number, end: number) => void;
  onMergeWithPrev: () => void;
  canMergeWithPrev: boolean;
  onDoneEditing: () => void;
}

export function LessonBlockShell(props: LessonBlockShellProps) {
  const {
    block,
    isFirst,
    isLast,
    isDisabled,
    onMoveUp,
    onMoveDown,
    onInsertAfter,
    onDelete,
    onDuplicate,
    onPatchData,
    onSplitRange,
    onMergeWithPrev,
    canMergeWithPrev,
    onDoneEditing,
  } = props;
  const meta = TYPE_META[block.block_type];
  const Icon = meta.Icon;
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const insertOptions = INSERT_OPTIONS[block.section];

  return (
    <article
      data-testid={`lesson-block-${block.id}`}
      data-mode="edit"
      className={cn(
        'group relative rounded-lg border-2 bg-white p-3 shadow-md ring-1 ring-slate-300 transition-all',
        meta.border,
      )}
    >
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded-md ring-1',
              meta.icon,
            )}
            title={meta.label}
            aria-label={meta.label}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </span>
          <span className="text-xs italic text-slate-500">en édition</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={onDoneEditing}
            disabled={isDisabled}
          >
            ✓ Terminé
          </Button>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst || isDisabled}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            aria-label="Monter"
            title="Monter"
          >
            ▲
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast || isDisabled}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            aria-label="Descendre"
            title="Descendre"
          >
            ▼
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            disabled={isDisabled}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            aria-label="Dupliquer"
            title="Dupliquer"
          >
            ⎘
          </button>
          {canMergeWithPrev ? (
            <button
              type="button"
              onClick={onMergeWithPrev}
              disabled={isDisabled}
              className="rounded p-1 text-slate-500 hover:bg-slate-100"
              aria-label="Fusionner avec le bloc précédent"
              title="Fusionner avec le bloc précédent (même type)"
            >
              ⤴
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDelete}
            disabled={isDisabled}
            className="rounded p-1 text-red-500 hover:bg-red-50 disabled:opacity-30"
            aria-label="Supprimer"
            title="Supprimer"
          >
            ✕
          </button>
        </div>
      </header>

      <div className="space-y-2">
        {renderLessonBlockEditor({ block, isDisabled, onPatchData, onSplitRange })}
      </div>

      <footer className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
        <div className="ml-auto flex items-center gap-1">
          {!showInsertMenu ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowInsertMenu(true)}
              disabled={isDisabled}
            >
              + Insérer après
            </Button>
          ) : (
            <>
              {insertOptions.map((opt) => (
                <Button
                  key={opt.type}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onInsertAfter(opt.type);
                    setShowInsertMenu(false);
                  }}
                  disabled={isDisabled}
                >
                  {opt.label}
                </Button>
              ))}
              <button
                type="button"
                onClick={() => setShowInsertMenu(false)}
                className="px-1 text-xs text-slate-500"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </footer>
    </article>
  );
}
